import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { AnalyticsTrigger, Button, Grid, GridColumn, Heading, Image, List, ListItem, ModalContent, ModalFooter, Spinner, Text } from 'telia-front-react';
import config from '../Config';
import LanguageStore from '../stores/LanguageStore';
import MovieDetailsStore, { MovieDetails } from '../stores/movierental/MovieDetailsStore';
import { ContentType } from '../stores/movierental/MovieRentalCatalogStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import MovieLength from './MovieLength';
import MovieResolutionsAndPrice from './MovieResolutionsAndPrice';

interface MovieDetailsProps {
  movieId: number;
  type: ContentType;
  image?: string;
  isFreeOfCharge?: boolean;
  youtubeUrl?: string;
  movieDetailsStore?: MovieDetailsStore;
  languageStore?: LanguageStore;
  rootCategoryId: string;
  specialPricePacketIds?: number[];
}

@observer
class MovieDetailsContainer extends React.Component<MovieDetailsProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  private readonly ROOT_CATEGORY_IDS_CHILDRENS_CORNER: string[] = ['49', '51', '52', '92', '110', '114'];
  private readonly ROOT_CATEGORY_IDS_ADDITIONAL_SERVICES: string[] = ['1006'];
  private readonly PACKET_IDS_GO3: number = 491;
  private readonly PACKET_IDS_HBO: number = 428;
  private readonly PACKET_IDS_NAT_GEO: number = 460;

  private resolveTeliaTvUrl(movieDetails: MovieDetails, language: string): string {
    const { type, rootCategoryId } = this.props;
    const languageSlug = language !== 'et' ? `/${language}` : '';
    const contentType = type === ContentType.ASSETS ? 'film' : 'sari';
    let themeParam = '';
    if (this.ROOT_CATEGORY_IDS_CHILDRENS_CORNER.includes(rootCategoryId)) {
      themeParam = '?t=kids';
    } else if (this.ROOT_CATEGORY_IDS_ADDITIONAL_SERVICES.includes(rootCategoryId)) {
      if (movieDetails.packetIds.includes(this.PACKET_IDS_GO3)) {
        themeParam = '?t=go3';
      } else if (movieDetails.packetIds.includes(this.PACKET_IDS_HBO)) {
        themeParam = '?t=hbo';
      } else if (movieDetails.packetIds.includes(this.PACKET_IDS_NAT_GEO)) {
        themeParam = '?t=natgeo';
      }
    }
    return `${config.teliaTvUrl}${languageSlug}/${contentType}/${movieDetails.id}${themeParam}`;
  }

  private static formatStringList(items: string[]): string {
    return items && items.join(', ');
  }

  private getFilmDataList(movieDetails: MovieDetails): JSX.Element {
    const translate = this.context.translate;
    const { type } = this.props;

    return (
      <List type="icon" size="small" direction="inline">
        <ListItem icon="#service-internet">
          {movieDetails.countries.length ? (
            <>
              <p className="text-tiny">{translate('movieRental.countries')}</p>
              <p>
                {MovieDetailsContainer.formatStringList(movieDetails.countries)}, {movieDetails.year}
              </p>
            </>
          ) : (
            <>
              <p className="text-tiny">{translate('movieRental.year')}</p>
              <p>{movieDetails.year}</p>
            </>
          )}
        </ListItem>
        <ListItem icon="#backup">
          <MovieLength movieDetails={movieDetails} type={type} />
        </ListItem>
        <ListItem icon="#volume">
          <p className="text-tiny">{translate('movieRental.audio')}</p>
          <p>{MovieDetailsContainer.formatStringList(movieDetails.audioTracks) || '-'}</p>
        </ListItem>
        <ListItem icon="#chat">
          <p className="text-tiny">{translate('movieRental.subtitles')}</p>
          <p>{MovieDetailsContainer.formatStringList(movieDetails.subtitles) || '-'}</p>
        </ListItem>
      </List>
    );
  }

  public render() {
    const translate = this.context.translate;
    const {
      languageStore: { currentLanguage },
      isFreeOfCharge,
      specialPricePacketIds,
      movieDetailsStore,
      movieId,
      image,
      type,
    } = this.props;

    if (movieDetailsStore.isFetchingMovieDetails(movieId, type)) {
      return (
        <ModalContent>
          <Spinner />
        </ModalContent>
      );
    }

    const movieDetails = movieDetailsStore.getMovieDetails(movieId, type);
    if (!movieDetails) {
      return (
        <ModalContent>
          <Heading className={'center'} level={4}>
            {translate(`movieRental.${type}.unableToLoadDetails`)}
          </Heading>
        </ModalContent>
      );
    }

    const urls: string[] = [];
    if (image) {
      urls.push(image);
    } else {
      if (movieDetails.images?.poster) {
        urls.push(`${movieDetails.images.poster} 350w`);
      }
      if (movieDetails.images?.large) {
        urls.push(`${movieDetails.images.large} 280w`);
      }
      if (!urls.length) {
        urls.push('//place-hold.it/220x330');
      }
    }
    const srcset = urls.join();

    return (
      <>
        <ModalContent>
          <Text>
            <Grid noWrap={true}>
              <GridColumn width={['min']} className="hidden-md hidden-lg hidden-xl">
                <Image alt={movieDetails.title} srcset={srcset} width={86} align="center" />
              </GridColumn>
              <GridColumn width={['max']}>
                <Text>
                  <Heading level={1} className="h3">
                    {movieDetails.title}
                  </Heading>
                  <Heading level={2} className="h4 text__margin-none">
                    {movieDetails.originalTitle}
                  </Heading>
                </Text>
              </GridColumn>
            </Grid>
            <p>
              {movieDetails.description}
              &nbsp;
            </p>
            {this.getFilmDataList(movieDetails)}
          </Text>
        </ModalContent>
        <ModalFooter>
          <Grid align={['middle-xs', 'between-xs']}>
            <MovieResolutionsAndPrice
              isFreeOfCharge={isFreeOfCharge}
              price={movieDetails.price}
              type={type}
              resolutions={movieDetails.resolutions}
              packetIds={movieDetails.packetIds}
              specialPricePacketIds={specialPricePacketIds}
            />
            <GridColumn width={['xs']}>
              <AnalyticsTrigger
                trigger={{
                  id: `Link - View - ${movieDetails.title}`,
                  callback: 'onClick',
                }}
              >
                <Button
                  url={this.resolveTeliaTvUrl(movieDetails, currentLanguage)}
                  target="_blank"
                  text={translate('movieRental.teliaTvOrderButton')}
                  type="expressive"
                  variant="secondary"
                  iconRight="#arrow-right"
                />
              </AnalyticsTrigger>
            </GridColumn>
          </Grid>
        </ModalFooter>
      </>
    );
  }
}

export default inject('movieDetailsStore', 'languageStore')(MovieDetailsContainer);
